/**
 * ConfirmModal — Layer 3 template for confirmation dialogs.
 * Small bottom-sheet on mobile, centered card on desktop.
 */

import { useTranslation } from 'react-i18next';
import { Loader2Icon } from 'lucide-react';
import { BaseModal } from './BaseModal';
import { MODAL_COLORS } from './constants';
import type { ConfirmModalProps } from './types';

const VARIANT_COLORS = {
  danger: MODAL_COLORS.danger,
  warning: MODAL_COLORS.warning,
  info: MODAL_COLORS.header,
} as const;

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  variant = 'danger',
  isPending = false,
  confirmLabel,
  cancelLabel,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const buttonColor = VARIANT_COLORS[variant];

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      preventClose={isPending}
      mobileVariant="bottomsheet"
      maxWidth="max-w-sm"
      ariaLabel={title}
    >
      <div className="p-5 space-y-4">
        <h2
          className="text-lg font-bold text-center"
          style={{ color: MODAL_COLORS.text }}
        >
          {title}
        </h2>
        <p
          className="text-sm text-center"
          style={{ color: MODAL_COLORS.text, opacity: 0.8 }}
        >
          {message}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isPending}
            className="flex-1 py-2.5 rounded-xl font-semibold transition-all active:scale-[0.98] disabled:opacity-50 glass-btn"
            style={{
              color: MODAL_COLORS.text,
            }}
          >
            {cancelLabel || t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="flex-1 py-2.5 rounded-xl font-semibold transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 glass-btn"
            style={{
              backgroundColor: buttonColor,
              color: '#ffffff',
              boxShadow: isPending ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            {isPending && <Loader2Icon className="h-4 w-4 animate-spin" />}
            {confirmLabel || t('common.confirm')}
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
